import React, { useEffect } from 'react';
import apiClient from './services/apiClient'; // Importation de l'apiClient
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import Layout from './components/Layout'; // Importer le Layout
import ProtectedRoute from './components/ProtectedRoute';
import { AuthContext, AuthProvider } from './contexts/AuthContext';
import SubscriptionDashboard from './features/subscription/SubscriptionDashboard';
import DashboardPage from './pages/DashboardPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import MonitoringPage from './pages/MonitoringPage';
import ReduxDemo from './components/ReduxDemo';
import AuthMigrationDemo from './components/AuthMigrationDemo';
import ExternalRedirect from './components/ExternalRedirect';
import SkillsCoachRedirect from './pages/SkillsCoachRedirect';
import { AgentsPage } from './features/agents';
import { FAQDashboard } from './features/faq';
import { MetaSdkDiagnostic, WhatsAppSetup, WhatsAppSettings } from './features/whatsapp-config';
import WhatsAppDashboard from './features/whatsapp/pages/WhatsAppDashboard';
import performanceMonitor from './utils/performanceMonitor';

// Démarrer le monitoring de performance
if (process.env.NODE_ENV === 'development' || window.location.search.includes('monitor=true')) {
  performanceMonitor.startMonitoring();
  console.log('🚀 Performance monitoring activé pour intégration J+4');
}

// Composant pour gérer la redirection initiale
const InitialRedirect = () => {
  const { isAuthenticated } = React.useContext(AuthContext);
  // Si authentifié et sur la racine, redirige vers /dashboard, sinon vers /login
  // Si l'état d'authentification n'est pas encore déterminé, ne rien faire (ou afficher un loader)
  if (isAuthenticated === null) return null; // Ou un spinner de chargement
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

import { SubscriptionProvider } from './contexts/SubscriptionContext';

function App() {
  useEffect(() => {
    // Requête d'initialisation pour obtenir le cookie CSRF
    const initializeCsrf = async () => {
      try {
        await apiClient.get('/api/schema/'); // Endpoint GET qui ne nécessite pas d'auth
        console.log('CSRF cookie initialized');
      } catch (error) {
        console.error('Failed to initialize CSRF cookie:', error);
      }
    };

    initializeCsrf();
  }, []); // Le tableau vide assure que l'effet ne s'exécute qu'une seule fois
  return (
    <Router>
      <AuthProvider>
        <SubscriptionProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            {/* Routes protégées utilisant le Layout */}
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}> {/* Envelopper les routes protégées avec Layout */}
                <Route path="/" element={<InitialRedirect />} /> {/* Redirection initiale à l'intérieur du Layout */}
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/subscription" element={<SubscriptionDashboard />} />
                <Route path="/agents" element={<AgentsPage />} />
                <Route path="/faq" element={<FAQDashboard />} />
                <Route path="/whatsapp-config" element={<WhatsAppSetup />} />
                <Route path="/whatsapp/setup" element={<WhatsAppSetup />} />
                <Route path="/whatsapp/settings" element={<WhatsAppSettings />} />
                <Route path="/whatsapp/meta-sdk-diagnostic" element={<MetaSdkDiagnostic />} />
                <Route path="/whatsapp/conversations" element={<WhatsAppDashboard />} />
                <Route path="/monitoring" element={<MonitoringPage />} />
                <Route path="/redux-demo" element={<ReduxDemo />} />
                <Route path="/auth-migration" element={<AuthMigrationDemo />} />
                <Route
                  path="/genesis-coaching"
                  element={<ExternalRedirect to={import.meta.env.VITE_GENESIS_FRONTEND_URL || 'http://localhost:3002'} />}
                />
                <Route path="/skills-coach" element={<SkillsCoachRedirect />} />
                {/* Ajoutez d'autres routes protégées ici si nécessaire */}
              </Route>
            </Route>
            {/* Redirection pour les chemins non trouvés vers /dashboard si authentifié, sinon /login */}
            <Route path="*" element={<NavigateToCorrectRoute />} />
          </Routes>
        </SubscriptionProvider>
      </AuthProvider>
    </Router>
  );
}

// Composant pour gérer la redirection des chemins non trouvés
const NavigateToCorrectRoute = () => {
  const { isAuthenticated } = React.useContext(AuthContext);
  if (isAuthenticated === null) return null; // Ou un spinner
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

export default App;
